import { QueryClient, useQueryClient } from '@tanstack/react-query';

/**
 * Utility functions for cache invalidation
 */

export const invalidateCampaignRequestsCache = (queryClient: QueryClient) => {
    // Invalidate all campaign-requests queries
    queryClient.invalidateQueries({
        queryKey: ['campaign-requests'],
        exact: false, // This will invalidate all queries that start with 'campaign-requests'
    });

    // Also invalidate search filters cache since they might be affected
    queryClient.invalidateQueries({
        queryKey: ['campaign_search_filters'],
    });
};

export const invalidateCampaignRequestDetailCache = (queryClient: QueryClient, requestId: string | number) => {
    // Invalidate specific campaign request detail
    queryClient.invalidateQueries({
        queryKey: ['campaign-request', requestId],
    });
    
    // Also invalidate the list since the detail might affect list items
    invalidateCampaignRequestsCache(queryClient);
};

export const refreshCampaignRequestsCache = async (queryClient: QueryClient) => {
    await queryClient.refetchQueries({
        queryKey: ['campaign-requests'],
        type: 'active',
    });
};

export const invalidateCampaignApplicationsCache = (queryClient: QueryClient) => {
    // Invalidate all campaign-applications queries
    queryClient.invalidateQueries({
        queryKey: ['campaign-applications'],
        exact: false,
    });

    // Also invalidate search filters cache since they might be affected
    queryClient.invalidateQueries({
        queryKey: ['campaign_search_filters'],
    });
};

export const refreshCampaignApplicationsCache = async (queryClient: QueryClient) => {
    // Invalidate and force refetch of campaign applications
    // This clears both React Query cache and forces backend to bypass cache via nocache parameter
    await queryClient.invalidateQueries({
        queryKey: ['campaign-applications'],
        exact: false,
    });
    await queryClient.refetchQueries({
        queryKey: ['campaign-applications'],
        exact: false,
    });
};

// Both views of the One Time Contributions screen, so Refresh works whichever
// one is showing.
const ONE_TIME_CONTRIBUTION_KEYS = ['all-supporters', 'all-refunds'];

// invalidateQueries alone is enough: it refetches only the page on screen and
// marks every other cached page/filter combo stale, so each reloads when it is
// next shown. A follow-up refetchQueries would fetch the visible page a second
// time and re-request every combo browsed since the page loaded.
export const refreshAllSupportersCache = async (queryClient: QueryClient) => {
    await Promise.all(
        ONE_TIME_CONTRIBUTION_KEYS.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey: [queryKey] })
        )
    );
};

/**
 * Optimistic update for campaign request status changes
 */
export const updateCampaignRequestOptimistically = (
    queryClient: QueryClient,
    requestId: string | number,
    updates: Partial<any>
) => {
    // Update all campaign-requests queries that might contain this item
    queryClient.setQueriesData(
        { queryKey: ['campaign-requests'], exact: false },
        (oldData: any) => {
            if (!oldData || !oldData.results) return oldData;
            
            return {
                ...oldData,
                results: oldData.results.map((item: any) => 
                    item.id === requestId ? { ...item, ...updates } : item
                )
            };
        }
    );

    // Update the specific detail query if it exists
    queryClient.setQueryData(['campaign-request', requestId], (oldData: any) => {
        if (!oldData) return oldData;
        return { ...oldData, ...updates };
    });
};

/**
 * Hook for easy cache invalidation in components
 */
export const useCacheInvalidation = () => {
    const queryClient = useQueryClient();

    return {
        invalidateCampaignRequests: () => invalidateCampaignRequestsCache(queryClient),
        invalidateCampaignRequestDetail: (id: string | number) => 
            invalidateCampaignRequestDetailCache(queryClient, id),
        refreshCampaignRequests: () => refreshCampaignRequestsCache(queryClient),
        updateOptimistically: (id: string | number, updates: Partial<any>) =>
            updateCampaignRequestOptimistically(queryClient, id, updates),
        invalidateCampaignApplications: () => invalidateCampaignApplicationsCache(queryClient),
        refreshCampaignApplications: () => refreshCampaignApplicationsCache(queryClient),
        refreshOneTimeContributions: () => refreshAllSupportersCache(queryClient),
    };
};
